import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { AiOutlineLogout } from "react-icons/ai";
import { SheetClose } from "../ui/sheet";
import { axiosInstance } from "src/axios-instance";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { userTypes } from "src/enums/usersTpes";

export const LinkItem = ({ item, ...props }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const userRole = localStorage.getItem("role");

  const selectedKey =
    pathname === item.link ||
    (pathname.startsWith(item.link) && pathname[item.link.length] === "/");

  // Check if any submenu item is active
  const hasActiveSubmenu = item.submenu?.some(
    (subItem) =>
      pathname === subItem.link ||
      (pathname.startsWith(subItem.link) &&
        pathname[subItem.link.length] === "/")
  );

  // Filter submenu items based on user permissions
  const filteredSubmenu = item.submenu?.filter((subItem) => {
    // If item requires admin and user is not admin, hide it
    if (subItem.admin && userRole !== userTypes.ADMIN) {
      return false;
    }
    return true;
  });

  // Auto-open submenu if any submenu item is active
  React.useEffect(() => {
    if (hasActiveSubmenu) {
      setIsOpen(true);
    }
  }, [hasActiveSubmenu]);

  if (props.mobileNave) {
    return (
      <SheetClose asChild>
        {element({
          item,
          selectedKey,
          t,
          isOpen,
          setIsOpen,
          hasActiveSubmenu,
          filteredSubmenu,
          ...props,
        })}
      </SheetClose>
    );
  } else
    return element({
      item,
      selectedKey,
      t,
      isOpen,
      setIsOpen,
      hasActiveSubmenu,
      filteredSubmenu,
      ...props,
    });
};

const element = ({
  item,
  selectedKey,
  t,
  isOpen,
  setIsOpen,
  hasActiveSubmenu,
  filteredSubmenu,
  mobileNave,
}) => {
  // If item has submenu, render collapsible
  if (item.hasSubmenu && item.submenu) {
    // Don't render if no submenu items are visible after filtering
    if (!filteredSubmenu || filteredSubmenu.length === 0) {
      return null;
    }

    return (
      <div className="w-full">
        <Button
          variant="ghost"
          className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group ${
            selectedKey || hasActiveSubmenu
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className={`shrink-0 transition-transform duration-200 ${selectedKey || hasActiveSubmenu ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground"}`}>
              {item.icon}
            </span>
            <span className="truncate">{t(item.title)}</span>
          </div>
          <span className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""} ${selectedKey || hasActiveSubmenu ? "text-primary-foreground" : "text-muted-foreground"}`}>
            {localStorage.getItem("Lang") === "ar" ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        </Button>

        {isOpen && (
          <div className="ml-4 mt-1 space-y-0.5 animate-in slide-in-from-top-2 duration-200">
            {filteredSubmenu.map((subItem, index) => (
              <SubMenuItem key={index} item={subItem} mobileNave={mobileNave} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular link item
  return (
    <Link
      to={item.link}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group relative ${
        selectedKey
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
    >
      <span className={`shrink-0 transition-colors duration-200 ${selectedKey ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground"}`}>
        {item.icon}
      </span>
      <span className="truncate flex-1">{t(item.title)}</span>
      {selectedKey && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-foreground rounded-l-full" />
      )}
    </Link>
  );
};

// Submenu item component
const SubMenuItem = ({ item, mobileNave }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const userRole = localStorage.getItem("role");
  const { userTypes } = require("src/enums/usersTpes");

  // Check if user has permission to see this item
  if (item.admin && userRole !== userTypes.ADMIN) {
    return null;
  }

  const selectedKey =
    pathname === item.link ||
    (pathname.startsWith(item.link) && pathname[item.link.length] === "/");

  const linkElement = (
    <Link
      to={item.link}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 group relative ${
        selectedKey
          ? "bg-primary/15 text-primary border-l-2 border-primary"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      }`}
    >
      <span className={`shrink-0 transition-colors duration-200 ${selectedKey ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`}>
        {item.icon}
      </span>
      <span className="truncate flex-1">{t(item.title)}</span>
    </Link>
  );

  // Wrap with SheetClose only for mobile navigation
  if (mobileNave) {
    return <SheetClose asChild>{linkElement}</SheetClose>;
  }

  return linkElement;
};

export const LogOutButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const logOut = () => {
    axiosInstance.post("/logout").then(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("tenantId");
      localStorage.removeItem("premssions");
      localStorage.removeItem("tenantName");
      navigate("/login");
    });
  };
  
  return (
    <Button
      className="w-full flex items-center justify-start gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 bg-destructive/10 :bg-destructive/20 text-destructive border  border-destructive/20"
      variant="ghost"
      onClick={() => logOut()}
    >
      <AiOutlineLogout className="w-4 h-4 shrink-0" />
      <span className="flex-1 ">{t("gloabl.LogOut")}</span>
    </Button>
  );
};
